import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Chip,
  CircularProgress, IconButton, Tooltip, Avatar, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  Badge,
} from '@mui/material';
import {
  Refresh, ArrowBack, Send, CheckCircle, HourglassEmpty,
  QuestionAnswer, Person, Search, School,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';

const STATUS_STYLE = {
  open:     { bg: '#ffebee', color: '#c62828', label: 'Open' },
  answered: { bg: '#e8eaf6', color: '#1a237e', label: 'Answered' },
  closed:   { bg: '#e8f5e9', color: '#2e7d32', label: 'Closed' },
};
const PRIORITY_STYLE = {
  high:   { bg: '#ffebee', color: '#c62828' },
  medium: { bg: '#fff3e0', color: '#e65100' },
  low:    { bg: '#e8f5e9', color: '#2e7d32' },
};

const Bubble = ({ msg, isMe }) => (
  <Box sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 1.5 }}>
    <Box sx={{
      maxWidth: '72%', px: 2, py: 1.2, borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
      background: isMe ? 'linear-gradient(135deg,#1a237e,#1565c0)' : '#f5f6fa',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <Typography fontSize={11} fontWeight={700} sx={{ color: isMe ? 'rgba(255,255,255,0.7)' : '#1a237e', mb: 0.3 }}>
        {msg.sender_name || (isMe ? 'You' : msg.sender_type)}
      </Typography>
      <Typography fontSize={13} sx={{ color: isMe ? '#fff' : '#333', lineHeight: 1.5 }}>{msg.message}</Typography>
      <Typography fontSize={10} sx={{ color: isMe ? 'rgba(255,255,255,0.55)' : 'text.disabled', mt: 0.5, textAlign: 'right' }}>
        {msg.created_at ? dayjs(msg.created_at).format('DD MMM · hh:mm A') : ''}
      </Typography>
    </Box>
  </Box>
);

export default function Doubts() {
  const { user } = useAuth();
  const isAdmin = user?.role !== 'Teacher';
  const [view, setView] = useState(isAdmin ? 'teachers' : 'doubts');
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [doubts, setDoubts] = useState([]);
  const [doubtsLoading, setDoubtsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [chatInterval, setChatInterval] = useState(null);
  const bottomRef = useRef(null);
  const [acYear, setAcYear] = useState('');

  useEffect(() => {
    const loadAcYear = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {});
      const years = data?.academic_years || [];
      const current = years.find(y => y.is_current) || years[0];
      if (current) setAcYear(current.value || '');
    };
    loadAcYear();
  }, []);

  const loadTeachers = useCallback(async () => {
    if (!isAdmin) return;
    setTeachersLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getUsers, { status: 'Active' });
    const teacherList = (data?.users || []).filter(u => u.role === 'Teacher' && u.status === 'Active');

    const withCounts = await Promise.all(teacherList.map(async (t) => {
      try {
        const d = await InvokeApi(Config.main_url + apiList.getTeacherDoubts, { teacher_id: t.id, academic_year: acYear });
        const list = d?.doubts || [];
        return {
          ...t,
          doubtCount: list.length,
          openCount: list.filter(x => x.status === 'open').length,
          unreadCount: list.reduce((s, x) => s + (parseInt(x.unread_count) || 0), 0),
        };
      } catch {
        return { ...t, doubtCount: 0, openCount: 0, unreadCount: 0 };
      }
    }));
    setTeachers(withCounts);
    setTeachersLoading(false);
  }, [isAdmin, acYear]);

  useEffect(() => {
    if (isAdmin && acYear) loadTeachers();
  }, [loadTeachers, isAdmin, acYear]);

  const loadDoubts = useCallback(async (teacherId) => {
    setDoubtsLoading(true);
    const params = { academic_year: acYear };
    const tid = isAdmin ? teacherId : user?.adminId;
    if (tid) params.teacher_id = tid;
    const data = await InvokeApi(Config.main_url + apiList.getTeacherDoubts, params);
    setDoubts(data?.doubts || []);
    setDoubtsLoading(false);
  }, [acYear, isAdmin, user?.adminId]);

  useEffect(() => {
    if (view === 'doubts' && acYear) {
      loadDoubts(selectedTeacher?.id || null);
    }
  }, [view, acYear, loadDoubts, selectedTeacher]);

  const loadMessages = useCallback(async (doubtId) => {
    setMessagesLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getDoubtMessages, {
      doubt_id: doubtId,
      user_id: user?.adminId,
      user_type: isAdmin ? 'teacher' : 'teacher',
    });
    setMessages(data?.messages || []);
    setMessagesLoading(false);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [user?.adminId, isAdmin]);

  useEffect(() => {
    if (view === 'chat' && selectedDoubt) {
      loadMessages(selectedDoubt.id);
      const interval = setInterval(() => loadMessages(selectedDoubt.id), 30000); // was 8s, 30s saves ~270 connections/hour
      setChatInterval(interval);
      return () => {
        clearInterval(interval);
        setChatInterval(null);
      };
    }
  }, [view, selectedDoubt?.id, loadMessages]);

  useEffect(() => () => { if (chatInterval) clearInterval(chatInterval); }, [chatInterval]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openTeacherDoubts = (teacher) => {
    setSelectedTeacher(teacher);
    setView('doubts');
  };

  const openChat = (doubt) => {
    setSelectedDoubt(doubt);
    setView('chat');
  };

  const goBack = () => {
    if (view === 'chat') { setView('doubts'); setMessages([]); }
    else if (view === 'doubts' && isAdmin) { setView('teachers'); setSelectedTeacher(null); }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !selectedDoubt) return;
    setSending(true);
    await InvokeApi(Config.main_url + apiList.addDoubtMessage, {
      doubt_id: selectedDoubt.id,
      sender_id: user?.adminId,
      sender_type: 'teacher',
      message: newMsg.trim(),
    });
    setNewMsg('');
    setSending(false);
    loadMessages(selectedDoubt.id);
  };

  const handleStatusChange = async (doubtId, status) => {
    await InvokeApi(Config.main_url + apiList.updateDoubtStatus, { doubt_id: doubtId, status });
    if (view === 'chat') setSelectedDoubt(prev => ({ ...prev, status }));
    loadDoubts(isAdmin ? selectedTeacher?.id : user?.adminId);
  };

  const filteredDoubts = doubts.filter(d => {
    const matchStatus = statusFilter === 'all' || d.status === statusFilter;
    const q = searchQ.toLowerCase();
    const matchSearch = !q || d.doubt_title?.toLowerCase().includes(q) ||
      d.subject?.toLowerCase().includes(q) || d.student_name?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const renderTeachers = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Doubts / Q&A</Typography>
          <Typography variant="body2" color="text.secondary">Select a teacher to view student doubts</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadTeachers} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {teachersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : teachers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Person sx={{ fontSize: 64, color: '#e8eaf6', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={500}>No teachers found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Teacher', 'Email', 'Total Doubts', 'Open', 'Unread', 'Action'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {teachers.map((t, i) => (
                  <TableRow key={t.id || i} hover sx={{ cursor: 'pointer' }} onClick={() => openTeacherDoubts(t)}>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, background: t.doubtCount > 0 ? 'linear-gradient(135deg,#1a237e,#1565c0)' : '#ccc', fontSize: 13, fontWeight: 700 }}>
                          {(t.name || t.full_name || 'T')[0]}
                        </Avatar>
                        <Typography fontWeight={600} fontSize={13}>{t.name || t.full_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{t.email || '—'}</TableCell>
                    <TableCell>
                      <Chip label={t.doubtCount} size="small"
                        sx={{ background: t.doubtCount > 0 ? '#e8eaf6' : '#f5f6fa', color: t.doubtCount > 0 ? '#1a237e' : 'text.secondary', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      {t.openCount > 0 && (
                        <Chip label={t.openCount} size="small" sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {t.unreadCount > 0 && (
                        <Chip label={t.unreadCount + ' new'} size="small" sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" endIcon={<QuestionAnswer sx={{ fontSize: 14 }} />}
                        sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 1.5, fontWeight: 700, fontSize: 11 }}>
                        View Doubts
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );

  const renderDoubts = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {isAdmin && (
          <IconButton onClick={goBack} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><ArrowBack /></IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">
            {selectedTeacher ? `${selectedTeacher.name || selectedTeacher.full_name}'s Doubts` : 'Student Doubts'}
          </Typography>
          <Typography variant="body2" color="text.secondary">{filteredDoubts.length} doubts</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => loadDoubts(isAdmin ? selectedTeacher?.id : user?.adminId)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search by title, subject, student..." value={searchQ}
            onChange={e => setSearchQ(e.target.value)} sx={{ minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[['all', '#f5f6fa', 'text.secondary', 'All'], ['open', '#ffebee', '#c62828', 'Open'], ['answered', '#e8eaf6', '#1a237e', 'Answered'], ['closed', '#e8f5e9', '#2e7d32', 'Closed']].map(([val, bg, color, label]) => (
              <Chip key={val} label={label} onClick={() => setStatusFilter(val)}
                sx={{ fontWeight: 700, cursor: 'pointer',
                  background: statusFilter === val ? bg : '#f5f6fa',
                  color: statusFilter === val ? color : 'text.secondary',
                  border: statusFilter === val ? `1.5px solid ${color}` : '1.5px solid transparent' }} />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {doubtsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Student', 'Subject', 'Title', 'Priority', 'Status', 'Date', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDoubts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <QuestionAnswer sx={{ fontSize: 48, color: '#e8eaf6', mb: 1, display: 'block', mx: 'auto' }} />
                      <Typography color="text.secondary">No doubts found</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredDoubts.map((d, i) => {
                  const ss = STATUS_STYLE[d.status] || STATUS_STYLE.open;
                  const ps = PRIORITY_STYLE[d.priority] || PRIORITY_STYLE.medium;
                  return (
                    <TableRow key={d.id || i} hover>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 11, fontWeight: 700 }}>
                            {(d.student_name || 'S')[0]}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={12}>{d.student_name || `#${d.student_id}`}</Typography>
                            <Typography fontSize={10} color="text.secondary">{d.standard} - {d.section}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{d.subject}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography fontSize={12} fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.doubt_title}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.doubt_description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={d.priority || 'medium'} size="small"
                          sx={{ background: ps.bg, color: ps.color, fontWeight: 700, fontSize: 10, textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={ss.label} size="small"
                          sx={{ background: ss.bg, color: ss.color, fontWeight: 700, fontSize: 10 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {d.created_at ? dayjs(d.created_at).format('DD MMM YYYY') : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button size="small" variant="outlined" onClick={() => openChat(d)}
                            sx={{ borderRadius: 1.5, fontSize: 10, fontWeight: 700, color: '#1a237e', borderColor: '#1a237e', px: 1 }}>
                            Chat
                          </Button>
                          {d.status === 'open' && (
                            <Button size="small" variant="contained" startIcon={<CheckCircle sx={{ fontSize: 12 }} />}
                              onClick={() => handleStatusChange(d.id, 'answered')}
                              sx={{ background: '#2e7d32', borderRadius: 1.5, fontSize: 10, fontWeight: 700, px: 1 }}>
                              Answer
                            </Button>
                          )}
                          {d.status === 'answered' && (
                            <Button size="small" variant="contained"
                              onClick={() => handleStatusChange(d.id, 'closed')}
                              sx={{ background: '#1a237e', borderRadius: 1.5, fontSize: 10, fontWeight: 700, px: 1 }}>
                              Close
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );

  const renderChat = () => {
    const ss = STATUS_STYLE[selectedDoubt?.status] || STATUS_STYLE.open;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2, flexShrink: 0 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <IconButton onClick={goBack} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><ArrowBack /></IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={800} fontSize={15} color="#1a237e">{selectedDoubt?.doubt_title}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip label={selectedDoubt?.subject} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10 }} />
                  <Chip label={ss.label} size="small" sx={{ background: ss.bg, color: ss.color, fontWeight: 700, fontSize: 10 }} />
                  {selectedDoubt?.priority && (
                    <Chip label={selectedDoubt.priority} size="small"
                      sx={{ background: PRIORITY_STYLE[selectedDoubt.priority]?.bg || '#f5f6fa', color: PRIORITY_STYLE[selectedDoubt.priority]?.color || '#666', fontWeight: 700, fontSize: 10, textTransform: 'capitalize' }} />
                  )}
                  <Typography fontSize={11} color="text.secondary">
                    Student: <strong>{selectedDoubt?.student_name}</strong> · {selectedDoubt?.standard}-{selectedDoubt?.section}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedDoubt?.status === 'open' && (
                  <Button size="small" variant="contained" startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                    onClick={() => handleStatusChange(selectedDoubt.id, 'answered')}
                    sx={{ background: '#2e7d32', borderRadius: 2, fontWeight: 700, fontSize: 11 }}>
                    Mark Answered
                  </Button>
                )}
                {selectedDoubt?.status === 'answered' && (
                  <Button size="small" variant="contained"
                    onClick={() => handleStatusChange(selectedDoubt.id, 'closed')}
                    sx={{ background: '#1a237e', borderRadius: 2, fontWeight: 700, fontSize: 11 }}>
                    Close Doubt
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ mt: 1.5, p: 1.5, background: '#f5f6fa', borderRadius: 2, borderLeft: '3px solid #1a237e' }}>
              <Typography fontSize={12} color="text.secondary" fontWeight={600} mb={0.3}>Doubt Description</Typography>
              <Typography fontSize={13} color="#333">{selectedDoubt?.doubt_description}</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {messagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
            ) : messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <QuestionAnswer sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                <Typography color="text.secondary" fontSize={13}>No messages yet. Start the conversation!</Typography>
              </Box>
            ) : (
              messages.map((msg, i) => (
                <Bubble key={msg.id || i} msg={msg} isMe={msg.sender_type === 'teacher' || msg.sender_type === 'admin'} />
              ))
            )}
            <div ref={bottomRef} />
          </Box>

          <Divider />
          <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-end', background: '#fff' }}>
            <TextField
              fullWidth multiline maxRows={4} size="small"
              placeholder="Type your reply..."
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <IconButton onClick={handleSend} disabled={!newMsg.trim() || sending}
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', borderRadius: 2, width: 42, height: 42, flexShrink: 0,
                '&:hover': { background: 'linear-gradient(135deg,#0d47a1,#1565c0)' },
                '&:disabled': { background: '#e8eaf6', color: '#bbb' } }}>
              {sending ? <CircularProgress size={18} color="inherit" /> : <Send sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Card>
      </Box>
    );
  };

  return (
    <Box>
      {view === 'teachers' && renderTeachers()}
      {view === 'doubts' && renderDoubts()}
      {view === 'chat' && renderChat()}
    </Box>
  );
}
